import { DashboardKPI } from "@/modules/hub/features/MemberDashboard/types";
import type { ChangeTone } from "../types";
import {
  classifyThresholdZone,
  ThresholdZone,
} from "@/modules/hub/features/MemberDashboard/utils/thresholdZones";

export type KPIStatus = "on_target" | "watch" | "off_target" | "none";

export interface KPIStatusStyle {
  label: string;
  dot: string;
  value: string;
  stroke: string;
  border: string;
  topBorder: string;
  /** The tinted area under the sparkline. */
  area: string;
}

export const STATUS_STYLES: Record<KPIStatus, KPIStatusStyle> = {
  on_target: {
    label: "On target",
    dot: "bg-green-500 dark:bg-green-400",
    value: "text-green-600 dark:text-green-400",
    stroke: "stroke-green-500 dark:stroke-green-400",
    border: "border-l-green-500 dark:border-l-green-400",
    topBorder: "border-t-green-500 dark:border-t-green-400",
    area: "fill-green-500/10 dark:fill-green-400/10",
  },
  watch: {
    label: "Watch",
    dot: "bg-yellow-500 dark:bg-yellow-400",
    value: "text-yellow-600 dark:text-yellow-400",
    stroke: "stroke-yellow-500 dark:stroke-yellow-400",
    border: "border-l-yellow-500 dark:border-l-yellow-400",
    topBorder: "border-t-yellow-500 dark:border-t-yellow-400",
    area: "fill-yellow-500/10 dark:fill-yellow-400/10",
  },
  off_target: {
    label: "Off target",
    dot: "bg-red-500 dark:bg-red-400",
    value: "text-red-600 dark:text-red-400",
    stroke: "stroke-red-500 dark:stroke-red-400",
    border: "border-l-red-500 dark:border-l-red-400",
    topBorder: "border-t-red-500 dark:border-t-red-400",
    area: "fill-red-500/10 dark:fill-red-400/10",
  },
  none: {
    label: "No status",
    dot: "bg-slate-400 dark:bg-slate-500",
    value: "text-gray-500 dark:text-gray-400",
    stroke: "stroke-slate-400 dark:stroke-slate-500",
    border: "border-l-gray-200 dark:border-l-slate-700",
    topBorder: "border-t-gray-300 dark:border-t-slate-600",
    area: "fill-gray-400/10 dark:fill-slate-500/10",
  },
};

export const CHANGE_TONE_STYLES: Record<ChangeTone, string> = {
  good: "text-green-600 dark:text-green-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-gray-500 dark:text-gray-400",
};

const STATUS_BY_ZONE: Record<ThresholdZone, KPIStatus> = {
  "On target": "on_target",
  Watch: "watch",
  "Off target": "off_target",
};

/** Worst first. */
const STATUS_SEVERITY: KPIStatus[] = ["off_target", "watch", "on_target"];

export const resolveKPIStatus = (kpi: DashboardKPI): KPIStatus => {
  if (kpi.threshold === null || kpi.value === null) {
    return "none";
  }

  return STATUS_BY_ZONE[
    classifyThresholdZone(
      kpi.value,
      kpi.threshold.watchStartsAt,
      kpi.threshold.offTargetStartsAt,
      kpi.direction,
    )
  ];
};

export const resolveCompanyStatus = (statuses: KPIStatus[]): KPIStatus =>
  STATUS_SEVERITY.find((status) => statuses.includes(status)) ?? "none";
